import * as React from 'react';
import { useMemo, DragEvent, useRef, MouseEvent, useCallback, useState, RefObject } from 'react';
import AliceCarousel from 'react-alice-carousel';
import Image from 'next/image';
import { Button as FlowBiteButton } from 'flowbite-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { cn, customImageLoader, useMounted } from '@restworld/utils-common';

export const ImageCarousel = ({ restaurantPictures, variant }: ImageCarouselProps) => {
  const carouselRef = useRef<AliceCarousel>(null);
  const isMounted = useMounted();
  const items = useImageCarouselList({ restaurantPictures, isMounted, variant });
  const { onSlideChanged, activeIndex } = useActiveIndex();
  return (
    <Wrapper>
      <AliceCarousel
        activeIndex={activeIndex}
        onSlideChanged={(e) => onSlideChanged(e.item)}
        disableButtonsControls
        disableDotsControls
        touchTracking
        mouseTracking
        ref={carouselRef}
        controlsStrategy="alternate"
        items={items}
        responsive={{
          0: {
            items: variant === 'showcase' ? 1.1 : 1.2,
          },
          768: {
            items: variant === 'showcase' ? 1.1 : 3,
          },
        }}
      />
      {items.length > 1 && <CarouselNavigation carouselRef={carouselRef} />}
    </Wrapper>
  );
};
export default ImageCarousel;
// --> types
export interface ImageCarouselProps {
  restaurantPictures: string[];
  variant?: 'primary' | 'showcase';
}
export const CarouselNavigation = ({ carouselRef }: { carouselRef: RefObject<AliceCarousel | null> }) => {
  const navigate = ['prev', 'next'];
  const carouselNavigationHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>, navigate: string) => {
      e.preventDefault();
      if (navigate === 'prev') carouselRef?.current?.slidePrev(e);
      else carouselRef?.current?.slideNext(e);
    },
    [carouselRef]
  );
  return (
    <>
      {navigate.map((role) => (
        <NavigationButton key={role} navigate={role} onClick={(e) => carouselNavigationHandler(e, role)}>
          {role === navigate[0] ? <ChevronLeftIcon className="h-5" /> : <ChevronRightIcon className="h-5" />}
        </NavigationButton>
      ))}
    </>
  );
};
// parent --> child components
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative md:m-0 w-full', className)} {...props} />
);
const NavigationButton = ({
  navigate,
  className,
  ...props
}: { navigate: string } & React.ComponentProps<typeof FlowBiteButton>) => (
  <FlowBiteButton
    className={cn(
      'absolute top-[44%] -translate-y-2/4',
      'text-white w-8 h-8 rounded-full sm:w-10 sm:h-10 bg-gray-600/30 hover:bg-gray-600/50 focus:outline-none',
      navigate === 'prev' ? 'left-3.5' : 'right-3.5',
      className
    )}
    {...props}
  />
);

const useImageCarouselList = ({ restaurantPictures, isMounted, variant }: ImageCarouselList) => {
  const handleDragStart = (e: DragEvent<HTMLImageElement>) => e.preventDefault();
  const items = useMemo(
    () =>
      restaurantPictures.map((image, i) => (
        <span key={image}>
          <StyledImage
            loader={customImageLoader}
            data-value={i + 1}
            className="item"
            alt={`${'img' + i}`}
            src={image}
            onDragStart={handleDragStart}
            width={300}
            height={300}
            variant={variant}
          />
        </span>
      )),
    [restaurantPictures, variant]
  );
  return isMounted ? items : [];
};

interface ImageCarouselList extends ImageCarouselProps {
  isMounted: boolean;
}
export const useActiveIndex = (defaultIndex = 0) => {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const onSlideChanged = useCallback((index: number) => setActiveIndex(index), []);
  return {
    activeIndex,
    onSlideChanged,
  };
};

const StyledImage = ({
  variant,
  className,
  ...props
}: Pick<ImageCarouselProps, 'variant'> & React.ComponentProps<typeof Image>) => (
  <Image
    className={cn(
      'px-1 h-52 rounded-lg object-cover',
      variant === 'showcase' && 'w-full min-w-full h-[27.188rem] px-0 rounded-3.75',
      className
    )}
    {...props}
  />
);
